import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import { adminApi, AuditRecord } from '../service/adminApiService';
import { FilterBar } from '../components/FilterBar';
import { PaginationControls } from '../components/PaginationControls';
import { AuditDetailDrawer } from '../components/AuditDetailDrawer';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

const ACTION_OPTIONS = [
  '',
  'create_group',
  'update_group',
  'delete_group',
  'upsert_user',
  'delete_user',
  'bulk_import_users',
  'bulk_delete_users',
  'create_template',
  'update_template',
  'delete_template',
  'create_department',
  'update_department',
  'delete_department',
  'rbac_exported',
];

export const AuditLogPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const [items, setItems] = useState<AuditRecord[]>([]);
  const [tokens, setTokens] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const actor = params.get('actor') || '';
  const action = params.get('action') || '';
  const target = params.get('target') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
      setPageIndex(0);
      setTokens([null]);
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams({}, { replace: true });
    setPageIndex(0);
    setTokens([null]);
  }, [setParams]);

  const fetchPage = useCallback(
    async (tokenIndex: number) => {
      setLoading(true);
      setError(null);
      try {
        const token = tokens[tokenIndex] || undefined;
        const response = await adminApi.listAudit({
          next_token: token,
          actor: actor || undefined,
          action: action || undefined,
          target: target || undefined,
          from: from || undefined,
          to: to || undefined,
        });
        setItems(response.items);
        setNextToken(response.next_token);
      } catch (err: any) {
        setError(err?.message || 'Failed to load audit log');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tokens, actor, action, target, from, to],
  );

  useEffect(() => {
    fetchPage(pageIndex);
  }, [fetchPage, pageIndex]);

  const activeCount = useMemo(
    () => [actor, action, target, from, to].filter((v) => v && v.length > 0).length,
    [actor, action, target, from, to],
  );

  return (
    <Column spacing="large">
      <PageHeader
        title="Audit log"
        subtitle="Every admin create, update, delete, and export is recorded here for two years."
      />

      <Section>
      <FilterBar activeCount={activeCount} onClear={clearFilters}>
        <div style={{ minWidth: 180 }}>
          <Input
            type="text"
            value={actor}
            onChange={(v: string) => setParam('actor', v || null)}
            placeholder="Actor alias"
            aria-label="Filter by actor"
          />
        </div>
        <Select
          value={action}
          onChange={(v: string) => setParam('action', v || null)}
          placeholder="Action: any"
          aria-label="Filter by action"
          width={220}
        >
          {ACTION_OPTIONS.map((a) => (
            <SelectOption key={a || 'any'} value={a} label={a || 'Any'} />
          ))}
        </Select>
        <div style={{ minWidth: 200 }}>
          <Input
            type="text"
            value={target}
            onChange={(v: string) => setParam('target', v || null)}
            placeholder="Target ID"
            aria-label="Filter by target"
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Input
            type="text"
            value={from}
            onChange={(v: string) => setParam('from', v || null)}
            placeholder="From (ISO-8601)"
            aria-label="From timestamp"
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Input
            type="text"
            value={to}
            onChange={(v: string) => setParam('to', v || null)}
            placeholder="To (ISO-8601)"
            aria-label="To timestamp"
          />
        </div>
      </FilterBar>

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader type="circular" size="large" />
        </Row>
      ) : items.length === 0 ? (
        <Alert type="neutral">No audit events match the current filters.</Alert>
      ) : (
        <Column spacing="none">
          <Table headerRows={1} showDividers={true} aria-label="Audit records">
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
            {items.map((r) => (
              <TableRow key={r.change_id}>
                <TableCell>
                  <Text type="b200">{r.timestamp}</Text>
                </TableCell>
                <TableCell>{r.actor_alias}</TableCell>
                <TableCell>{r.action}</TableCell>
                <TableCell>
                  <Text type="b200">
                    {r.target_type}: {r.target_id}
                  </Text>
                </TableCell>
                <TableCell>
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() => setExpanded(expanded === r.change_id ? null : r.change_id)}
                  >
                    {expanded === r.change_id ? 'Hide' : 'Show'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </Table>
          {expanded && (
            <>
              {items
                .filter((r) => r.change_id === expanded)
                .map((r) => (
                  <AuditDetailDrawer key={r.change_id} record={r} />
                ))}
            </>
          )}
        </Column>
      )}

      <PaginationControls
        currentIndex={pageIndex}
        hasPrevious={pageIndex > 0}
        hasNext={!!nextToken}
        onPrevious={() => setPageIndex(pageIndex - 1)}
        onNext={() => {
          if (!nextToken) return;
          const newTokens = [...tokens];
          newTokens[pageIndex + 1] = nextToken;
          setTokens(newTokens);
          setPageIndex(pageIndex + 1);
        }}
        itemCount={items.length}
      />
      </Section>
    </Column>
  );
};
